import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

interface BugItem {
  _id: string;
  bugKey: string;
  summary: string;
  assignTo: Array<{
    _id: string;
    fullName: string;
  }>;
  bugStatus: {
    _id: string;
    statusCode: string;
    status: string;
  };
  severity: string;
  type: string;
  createdBy: {
    _id: string;
    fullName: string;
  };
  project: {
    _id: string;
    name: string;
    code: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface Sprint {
  _id: string;
  name: string;
  status: string;
  startDate?: string;
  endDate?: string;
  goal?: string;
}

interface UseBulkMoveProps {
  sprints: Sprint[];
  containerSelections: { [key: string]: Set<string> };
  setContainerSelections: React.Dispatch<
    React.SetStateAction<{ [key: string]: Set<string> }>
  >;
  sprintTasksData: { [key: string]: any };
  setSprintTasksData: React.Dispatch<
    React.SetStateAction<{ [key: string]: any }>
  >;
  updateSprintStatusMutation: any;
  refetchBugList: () => void;
  findTask: (taskId: string) => BugItem | null;
  getActiveContainer: () => string | null;
}

export const useBulkMove = ({
  sprints,
  containerSelections,
  setContainerSelections,
  sprintTasksData,
  setSprintTasksData,
  updateSprintStatusMutation,
  refetchBugList,
  findTask,
  getActiveContainer,
}: UseBulkMoveProps) => {
  const { t } = useTranslation(["bugherd"]);
  const [moveToMenuAnchorEl, setMoveToMenuAnchorEl] =
    useState<null | HTMLElement>(null);

  // Get available move destinations
  const getAvailableMoveDestinations = useCallback(() => {
    const activeContainer = getActiveContainer();
    if (!activeContainer) return [];

    const destinations: Array<{
      id: string;
      label: string;
      type: "sprint" | "backlog";
    }> = [];

    if (activeContainer === "backlog") {
      // If backlog tasks are selected, show available sprints
      sprints.forEach((sprint: Sprint) => {
        destinations.push({
          id: `sprint-${sprint._id}`,
          label: sprint.name,
          type: "sprint",
        });
      });
    } else if (activeContainer.startsWith("sprint-")) {
      // If sprint tasks are selected, show backlog and other sprints
      destinations.push({
        id: "backlog",
        label: t("Backlog"),
        type: "backlog",
      });

      const currentSprintId = activeContainer.replace("sprint-", "");
      sprints.forEach((sprint: Sprint) => {
        if (sprint._id !== currentSprintId) {
          destinations.push({
            id: `sprint-${sprint._id}`,
            label: sprint.name,
            type: "sprint",
          });
        }
      });
    }

    return destinations;
  }, [getActiveContainer, sprints, t]);

  // Update local state immediately for multiple tasks
  const updateLocalStateMultiple = useCallback(
    (
      taskIds: string[],
      fromContainer: string,
      toContainer: string,
      tasks: BugItem[]
    ) => {
      if (fromContainer === "backlog" && toContainer.startsWith("sprint-")) {
        // Moving from backlog to sprint
        const targetSprintId = toContainer.replace("sprint-", "");

        setSprintTasksData((prev) => {
          const currentSprintData = prev[targetSprintId] || {
            data: { decryptedData: { items: [], total: 0 } },
          };
          const currentItems =
            currentSprintData.data?.decryptedData?.items || [];

          // Filter out tasks that already exist to avoid duplicates
          const existingTaskIds = currentItems.map((item: BugItem) => item._id);
          const newTasks = tasks.filter(
            (task) => !existingTaskIds.includes(task._id)
          );

          if (newTasks.length === 0) return prev;

          return {
            ...prev,
            [targetSprintId]: {
              ...currentSprintData,
              data: {
                ...currentSprintData.data,
                decryptedData: {
                  ...currentSprintData.data.decryptedData,
                  items: [...currentItems, ...newTasks],
                  total:
                    (currentSprintData.data?.decryptedData?.total || 0) +
                    newTasks.length,
                },
              },
            },
          };
        });
      } else if (
        fromContainer.startsWith("sprint-") &&
        toContainer === "backlog"
      ) {
        // Moving from sprint to backlog
        const sourceSprintId = fromContainer.replace("sprint-", "");

        setSprintTasksData((prev) => {
          const currentSprintData = prev[sourceSprintId];
          if (!currentSprintData) return prev;

          const currentItems =
            currentSprintData.data?.decryptedData?.items || [];
          const filteredItems = currentItems.filter(
            (item: BugItem) => !taskIds.includes(item._id)
          );

          return {
            ...prev,
            [sourceSprintId]: {
              ...currentSprintData,
              data: {
                ...currentSprintData.data,
                decryptedData: {
                  ...currentSprintData.data.decryptedData,
                  items: filteredItems,
                  total: Math.max(
                    0,
                    (currentSprintData.data?.decryptedData?.total || 0) -
                      taskIds.length
                  ),
                },
              },
            },
          };
        });
      } else if (
        fromContainer.startsWith("sprint-") &&
        toContainer.startsWith("sprint-")
      ) {
        // Moving between sprints
        const sourceSprintId = fromContainer.replace("sprint-", "");
        const targetSprintId = toContainer.replace("sprint-", "");

        setSprintTasksData((prev) => {
          const sourceSprintData = prev[sourceSprintId];
          const targetSprintData = prev[targetSprintId] || {
            data: { decryptedData: { items: [], total: 0 } },
          };

          if (!sourceSprintData) return prev;

          // Remove from source
          const sourceItems = sourceSprintData.data?.decryptedData?.items || [];
          const filteredSourceItems = sourceItems.filter(
            (item: BugItem) => !taskIds.includes(item._id)
          );

          // Add to target
          const targetItems = targetSprintData.data?.decryptedData?.items || [];
          const existingTargetTaskIds = targetItems.map(
            (item: BugItem) => item._id
          );
          const newTasks = tasks.filter(
            (task) => !existingTargetTaskIds.includes(task._id)
          );

          return {
            ...prev,
            [sourceSprintId]: {
              ...sourceSprintData,
              data: {
                ...sourceSprintData.data,
                decryptedData: {
                  ...sourceSprintData.data.decryptedData,
                  items: filteredSourceItems,
                  total: Math.max(
                    0,
                    (sourceSprintData.data?.decryptedData?.total || 0) -
                      taskIds.length
                  ),
                },
              },
            },
            [targetSprintId]: {
              ...targetSprintData,
              data: {
                ...targetSprintData.data,
                decryptedData: {
                  ...targetSprintData.data.decryptedData,
                  items: [...targetItems, ...newTasks],
                  total:
                    (targetSprintData.data?.decryptedData?.total || 0) +
                    newTasks.length,
                },
              },
            },
          };
        });
      }
    },
    [setSprintTasksData]
  );

  // Get sprint by container ID
  const getSprintByContainerId = useCallback(
    (containerId: string): Sprint | null => {
      if (containerId === "backlog") return null;
      const sprintId = containerId.replace("sprint-", "");
      return sprints.find((sprint) => sprint._id === sprintId) || null;
    },
    [sprints]
  );

  // Handle bulk move using the same logic as drag and drop
  const handleBulkMove = useCallback(
    async (targetContainerId: string) => {
      const activeContainer = getActiveContainer();
      if (!activeContainer) return;

      const selectedTaskIds: string[] = Array.from(
        containerSelections[activeContainer] || new Set()
      );
      if (selectedTaskIds.length === 0) return;

      // Get task objects
      const taskObjects = selectedTaskIds
        .map((id) => findTask(id))
        .filter(Boolean) as BugItem[];

      // 1. IMMEDIATE LOCAL STATE UPDATE
      updateLocalStateMultiple(
        selectedTaskIds,
        activeContainer,
        targetContainerId,
        taskObjects
      );

      // Determine the API payload based on move type
      let apiPayload: any = {};

      if (
        activeContainer === "backlog" &&
        targetContainerId.startsWith("sprint-")
      ) {
        // Moving from backlog to sprint
        const targetSprint = getSprintByContainerId(targetContainerId);
        if (!targetSprint) {
          console.error("Target sprint not found");
          return;
        }

        apiPayload = {
          status: targetSprint.status,
          sprintId: targetSprint._id,
          bugs: selectedTaskIds,
        };
      } else if (
        activeContainer.startsWith("sprint-") &&
        targetContainerId === "backlog"
      ) {
        // Moving from sprint to backlog
        const sourceSprint = getSprintByContainerId(activeContainer);
        if (!sourceSprint) {
          console.error("Source sprint not found");
          return;
        }

        apiPayload = {
          status: sourceSprint.status,
          sprintId: sourceSprint._id,
          bugs: selectedTaskIds,
          moveItems: "BACKLOG",
        };
      } else if (
        activeContainer.startsWith("sprint-") &&
        targetContainerId.startsWith("sprint-")
      ) {
        // Moving between sprints
        const targetSprint = getSprintByContainerId(targetContainerId);
        if (!targetSprint) {
          console.error("Target sprint not found");
          return;
        }

        apiPayload = {
          status: targetSprint.status,
          sprintId: targetSprint._id,
          bugs: selectedTaskIds,
        };
      }

      try {
        // 2. BACKGROUND API CALL
        await updateSprintStatusMutation(apiPayload).unwrap();

        const taskCount = selectedTaskIds.length;
        toast.success(
          taskCount === 1
            ? t("Task moved successfully")
            : t("1 tasks moved successfully", { count: taskCount })
        );

        // 3. Clear selections from source container
        setContainerSelections((prev) => {
          const updated = { ...prev };
          delete updated[activeContainer];
          return updated;
        });

        // 4. Refresh backlog data only when moving to/from backlog
        if (activeContainer === "backlog" || targetContainerId === "backlog") {
          refetchBugList();
        }

        // Close the menu
        setMoveToMenuAnchorEl(null);
      } catch (error) {
        console.error("Error moving tasks:", error);
        toast.error(t("Failed to move tasks"));

        // 5. REVERT LOCAL STATE ON ERROR
        updateLocalStateMultiple(
          selectedTaskIds,
          targetContainerId,
          activeContainer,
          taskObjects
        );

        // Refresh data to ensure consistency
        if (activeContainer === "backlog" || targetContainerId === "backlog") {
          refetchBugList();
        }
      }
    },
    [
      getActiveContainer,
      containerSelections,
      findTask,
      updateLocalStateMultiple,
      getSprintByContainerId,
      updateSprintStatusMutation,
      t,
      setContainerSelections,
      refetchBugList,
    ]
  );

  return {
    // State
    moveToMenuAnchorEl,
    setMoveToMenuAnchorEl,

    // Functions
    getAvailableMoveDestinations,
    handleBulkMove,
  };
};
